/**
 * Public Blog Viewer Route
 *
 * Read-only standalone page for a published blog companion. Fetches
 * the rendered HTML from the api-server's public /blog-share/:token
 * endpoint and renders it. The api-server is the source of truth for
 * the actual page content (so social crawlers hitting that URL get OG
 * meta directly).
 *
 * No authentication required — public route.
 */

import { createFileRoute, notFound } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export const Route = createFileRoute('/blog/$shareToken')({
  component: PublicBlogViewer,
  loader: async ({ params }) => {
    const { shareToken } = params;
    if (!shareToken) {
      throw notFound();
    }
    return { shareToken };
  },
  notFoundComponent: () => (
    <div className="w-full min-h-screen bg-background flex items-center justify-center">
      <div className="text-center px-6 py-12">
        <h1 className="text-xl font-semibold mb-2">Blog not found</h1>
        <p className="text-sm text-muted-foreground mb-6">
          This blog link is no longer available.
        </p>
        <a
          href="https://4klabs.ai"
          className="text-sm text-primary hover:underline font-medium"
        >
          Visit 4K Labs
        </a>
      </div>
    </div>
  ),
});

function PublicBlogViewer() {
  const { shareToken } = Route.useLoaderData();
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/blog-share/${shareToken}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((body) => {
        if (cancelled) return;
        setHtml(body);
        // Promote the page <title> from the rendered HTML for tab UX.
        const titleMatch = body.match(/<title>([^<]+)<\/title>/i);
        if (titleMatch) document.title = titleMatch[1];
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[blog-viewer] failed to load', err);
        setError('Could not load this blog.');
      });
    return () => {
      cancelled = true;
    };
  }, [shareToken]);

  if (error) {
    return (
      <div className="w-full min-h-screen bg-background flex items-center justify-center">
        <div className="text-center px-6 py-12">
          <h1 className="text-xl font-semibold mb-2">Blog unavailable</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!html) {
    return (
      <div className="w-full min-h-screen bg-background flex items-center justify-center">
        <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 border-t-foreground animate-spin" />
      </div>
    );
  }

  // Render the api-server's full HTML in an iframe via srcDoc. This
  // gives byte-identical fidelity to what social crawlers will see when
  // hitting the api-server URL directly. Iframe sandbox allows scripts
  // (the embedded video iframe needs them).
  return (
    <iframe
      title="Blog post"
      srcDoc={html}
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
      allow="autoplay; fullscreen; clipboard-write"
      className="w-screen h-screen border-0 block"
    />
  );
}
